import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import {
  Card,
  Skeleton,
  Box,
  useTheme,
  alpha
} from "@mui/material";

import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import ChartFilter from "./Filter";
import OrderFilter from "./OrderFilter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const AveragePersonsByFloor = () => {
  const theme = useTheme();
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);
  const childRef = useRef(null);

  const [filter, setFilter] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const url = `analytics/floors/average-people`;
      const { data } = await axios.get(`${baseURL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...filter },
      });
      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching floors data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Chart Data
  const labels = dataList.map((item) => item.floor_level);
  const values = dataList.map((item) => item.avg_person_count);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Average Persons",
        data: values,
        backgroundColor: alpha(theme.palette.primary.main, 0.6),
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { color: theme.palette.text.primary } },
      title: { display: true, text: "Average Number of Persons by Floor Level", color: theme.palette.text.primary },
      tooltip: {
        callbacks: {
          label: (ctx) => `Avg Persons: ${ctx.raw.toFixed(2)}`,
        },
      },
      datalabels: {
        anchor: "center",
        align: "center",
        color: theme.palette.getContrastText(alpha(theme.palette.primary.main, 0.6)),
        font: { weight: "bold", size: 12 },
        formatter: (val) => val.toFixed(2),
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: { display: true, text: "Average Person Count", color: theme.palette.text.primary }
      },
      x: { 
        ticks: { color: theme.palette.text.primary },
        grid: { color: theme.palette.divider },
        title: { display: true, text: "Floor Level", color: theme.palette.text.primary }
      },
    },
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />,
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
      ) : (
        <Box sx={{ height: 500 }}>
          <Bar data={chartData} options={options} />
        </Box>
      )}
    </Card>
  );
};

export default AveragePersonsByFloor;
